import React, { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

// ──────────────────────────────────────────────────────────────────
// Cinematic cursor-follow light-leak.
//
// Renders a soft cyan radial gradient that trails the pointer inside
// the container referenced by `targetRef`. Uses `mix-blend-mode: screen`
// to bloom over the hero image without darkening it.
//
// Skipped on touch devices.
// ──────────────────────────────────────────────────────────────────
export default function HeroCursorTrail({ targetRef, size = 460 }) {
  const [visible, setVisible] = useState(false);
  const x = useMotionValue(-size);
  const y = useMotionValue(-size);
  // Slow spring on purpose — this is the "trail" that lags the cursor.
  const sx = useSpring(x, { stiffness: 55, damping: 20, mass: 1.1 });
  const sy = useSpring(y, { stiffness: 55, damping: 20, mass: 1.1 });

  useEffect(() => {
    const el = targetRef?.current;
    if (!el) return undefined;
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!mq.matches) return undefined;

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      x.set(e.clientX - r.left);
      y.set(e.clientY - r.top);
    };
    const onEnter = (e) => {
      const r = el.getBoundingClientRect();
      x.set(e.clientX - r.left);
      y.set(e.clientY - r.top);
      setVisible(true);
    };
    const onLeave = () => setVisible(false);

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [targetRef, x, y, size]);

  return (
    <motion.div
      aria-hidden
      data-testid="hero-cursor-trail"
      className="pointer-events-none absolute top-0 left-0 z-[6] hidden md:block"
      style={{ x: sx, y: sy }}
    >
      <div
        style={{
          transform: "translate(-50%, -50%)",
          width: size,
          height: size,
          background:
            "radial-gradient(closest-side, rgba(103,232,249,0.42), rgba(56,189,248,0.18) 42%, transparent 72%)",
          filter: "blur(24px)",
          mixBlendMode: "screen",
          opacity: visible ? 1 : 0,
          transition: "opacity 500ms ease",
          willChange: "opacity, transform",
        }}
      />
    </motion.div>
  );
}
